import { atom } from 'jotai';
import type { StreamChat } from 'stream-chat';

/**
 * App-wide chat state atoms.
 *
 * Client lifecycle and unread count are written by ChatConnection (mounted
 * in _app.tsx). Drawer atoms are read/written by useChatDrawer() and the
 * future ChatDrawer component (Phase 5).
 *
 * Phase 6: chatClientAtom becomes chatClientsAtom (Map<apiKey, StreamChat>),
 * chatUnreadCountAtom becomes a derived sum of per-client counts. Consumer
 * code (Navbar, messages page) doesn't change — they read the same atom type.
 */

/**
 * The connected StreamChat client instance.
 *
 * Written by ChatConnection after connectUser() succeeds.
 * Read by ChatMessagesView (/messages page) and ChatDrawer (Phase 5).
 */
export const chatClientAtom = atom<StreamChat | null>(null);

/**
 * Total unread message count across all channels.
 *
 * Seeded from connectUser() response, then updated in real-time via
 * GetStream WebSocket events (every event carries total_unread_count).
 * Read by the Navbar to render the Messages badge.
 */
export const chatUnreadCountAtom = atom(0);

/** Connection error message, null when healthy. */
export const chatConnectionErrorAtom = atom<string | null>(null);

/** Whether the chat drawer (slide-over panel) is visible. */
export const chatDrawerOpenAtom = atom(false);

/**
 * Healthie patient ID to display in the chat drawer.
 *
 * The drawer calls `trpc.chat.getChannel({ patientId })` to resolve this into
 * a `{ channelId, apiKey }` pair. This indirection lets callers (e.g., "Send
 * Follow-Up" CTA) open the drawer with just the patient ID — they don't need
 * to know the channel or which GetStream app it belongs to.
 */
export const chatDrawerPatientIdAtom = atom<string | null>(null);

/** One-shot composer text, scoped to its recipient so it cannot leak across chats. */
export const chatDrawerDraftAtom = atom<{
  patientId: string;
  text: string;
} | null>(null);
